/**
 * Executive Cockpit — the founder/top-management landing (V3 screen 05).
 *
 * Spec: specs/v3/product-technical/05-executive-cockpit.md. The CEO's default
 * surface answers: *Is my strategic will becoming reality? What is changing?
 * What is blocked? Which decisions need me? Are deliverables being produced?* It
 * is a **curated executive read over the portfolio**, not a list of entities —
 * it tells the executive where to look. It defaults to the L3+ levels (bets,
 * outcomes, decisions, blockers, deliverables) and hides the machinery layer.
 *
 * Composition: this screen is a READ projection that composes the portfolio-wide
 * read endpoints the runtime already exposes — bets (movement), key results (KR
 * movement), requested decisions (awaiting me), the global action-card attention
 * stream (blockers / what needs me), and deliverable artifacts. It creates no
 * policy, credentials, effects, or business truth; every control here is
 * navigation or proposal, routed through the normal admit/gate/mutate path.
 *
 * Health (`at_risk`/`blocked`) is a derived reading, never a typed lifecycle
 * status. The screen prefers label + badge + icon before color.
 *
 * The body routes through the shared eight-state `RenderState` contract (S13):
 * loading skeletons keep the frame; an authorized-but-empty portfolio is the
 * shared `empty`; a 403 downgrades to `forbidden`; any other failure is a
 * recoverable inline `error`; and an `overflow` banner discloses how many
 * attention items exist beyond what is shown (layout stays stable).
 */
import * as React from "react";
import { useGo } from "@refinedev/core";

import type { ActionCard, Decision, KeyResult, Package } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";
import {
  RenderState,
  type RenderStateKind,
} from "@/components/app/RenderState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Field,
  FieldGrid,
  InspectorSection,
  RefLink,
} from "@/components/inspector";
import { api, ApiError } from "@/lib/api";
import { formatPercent, humanize, progressPercent } from "@/lib/format";

/** How many attention items the right column shows; overflow discloses the rest. */
const ATTENTION_PAGE_SIZE = 25;
/** How many movement / decision rows each left-column section shows. */
const ROW_LIMIT = 8;

interface ListEnvelope<T> {
  items: T[];
  total: number;
}

interface CockpitData {
  bets: Package[];
  keyResults: KeyResult[];
  requestedDecisions: Decision[];
  attention: ActionCard[];
  attentionTotal: number;
  deliverables: number;
  deliverablesTotal: number;
}

/** Fetch one list page, tolerating a not-built endpoint as an empty result. */
async function listOrEmpty<T>(
  path: string,
  query: Record<string, string | number | boolean>,
  signal: AbortSignal,
): Promise<ListEnvelope<T>> {
  try {
    const res = await api.get<ListEnvelope<T>>(path, { query, signal });
    return {
      items: Array.isArray(res?.items) ? res.items : [],
      total: typeof res?.total === "number" ? res.total : res?.items?.length ?? 0,
    };
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
      return { items: [], total: 0 };
    }
    throw err;
  }
}

function useExecutiveCockpit() {
  const [data, setData] = React.useState<CockpitData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<unknown>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setLoading(true);
    setError(null);

    Promise.all([
      // Bets are the strategy-layer facade over the unified `/packages` store
      // (package_type=bet); there is no standalone `/bets` API endpoint.
      listOrEmpty<Package>(
        "/packages",
        { pageSize: ROW_LIMIT, page: 1, package_type: "bet", sort: "updated_at", order: "desc" },
        signal,
      ),
      listOrEmpty<KeyResult>(
        "/key-results",
        { pageSize: ROW_LIMIT, page: 1, sort: "updated_at", order: "desc" },
        signal,
      ),
      listOrEmpty<Decision>(
        "/decisions",
        { pageSize: ROW_LIMIT, page: 1, status: "requested", sort: "created_at", order: "desc" },
        signal,
      ),
      listOrEmpty<ActionCard>(
        "/action-cards",
        { pageSize: ATTENTION_PAGE_SIZE, page: 1 },
        signal,
      ),
      listOrEmpty<{ id: string }>(
        "/artifacts",
        { pageSize: 1, page: 1, artifact_type: "deliverable" },
        signal,
      ),
    ])
      .then(([bets, keyResults, decisions, attention, deliverables]) => {
        if (signal.aborted) return;
        setData({
          bets: bets.items,
          keyResults: keyResults.items,
          requestedDecisions: decisions.items,
          attention: attention.items,
          attentionTotal: attention.total,
          deliverables: deliverables.items.length,
          deliverablesTotal: deliverables.total,
        });
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setError(err);
        setLoading(false);
      });

    return () => controller.abort();
  }, [reloadKey]);

  return { data, loading, error, reload };
}

/** A KPI tile: the current value with a quiet label. Movement-first by spec. */
function Kpi({ label, value }: { label: string; value: number | string }) {
  return (
    <Card data-testid="cockpit-kpi">
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <p className="font-mono text-lg tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

/** Health/severity badge derived from severity — a reading, not a status. */
function SeverityBadge({ severity }: { severity: string }) {
  const critical = severity === "critical" || severity === "high";
  return (
    <Badge variant={critical ? "destructive" : "outline"}>
      <Icon name={critical ? "reject" : "more"} aria-hidden />
      {humanize(severity)}
    </Badge>
  );
}

function BetRow({ bet }: { bet: Package }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
      <div className="min-w-0">
        <RefLink resource="bets" id={bet.id} label={bet.name} />
        <p className="mt-0.5 text-xs text-muted-foreground">{humanize(bet.maturity_stage)}</p>
      </div>
      <StatusBadge status={bet.status} />
    </div>
  );
}

function KeyResultRow({ kr }: { kr: KeyResult }) {
  const pct = progressPercent(kr.current_value, kr.target_value);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
      <div className="min-w-0">
        <RefLink resource="key-results" id={kr.id} label={kr.title} />
        <p className="mt-0.5 text-xs text-muted-foreground">
          {kr.current_value ?? "—"} / {kr.target_value ?? "—"} {kr.unit ?? ""}
        </p>
      </div>
      <Badge variant="secondary">{formatPercent(pct / 100)}</Badge>
    </div>
  );
}

function DecisionRow({ decision }: { decision: Decision }) {
  const go = useGo();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
      <div className="min-w-0">
        <RefLink resource="decisions" id={decision.id} label={decision.title} />
        <p className="mt-0.5 text-xs text-muted-foreground">
          {decision.decision_type ? humanize(decision.decision_type) : "Decision"}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => go({ to: `/decisions/${decision.id}/room` })}
      >
        <Icon name="decisions" data-icon="inline-start" aria-hidden />
        Open Room
      </Button>
    </div>
  );
}

function AttentionRow({ card }: { card: ActionCard }) {
  const go = useGo();
  return (
    <div
      data-testid="cockpit-attention"
      className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-border px-3 py-2"
    >
      <button
        type="button"
        className="min-w-0 text-left"
        onClick={() => go({ to: `/action-cards/${card.id}` })}
      >
        <span className="text-sm font-medium hover:underline [overflow-wrap:anywhere]">
          {card.title}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground [overflow-wrap:anywhere]">
          {humanize(card.suggested_action_type)}
        </span>
      </button>
      <SeverityBadge severity={card.severity} />
    </div>
  );
}

function CockpitContent({
  data,
  reload,
}: {
  data: CockpitData;
  reload: () => void;
}) {
  const go = useGo();
  const movingBets = data.bets.filter((bet) => bet.status === "active").length;
  const blockers = data.attention.filter(
    (card) => card.severity === "high" || card.severity === "critical",
  ).length;
  const attentionOverflow = Math.max(0, data.attentionTotal - data.attention.length);

  return (
    <RenderState
      state={attentionOverflow > 0 ? "overflow" : "normal"}
      overflowCount={attentionOverflow}
      overflowMessage={
        attentionOverflow > 0
          ? `${attentionOverflow} more attention item${attentionOverflow === 1 ? "" : "s"} beyond the ${ATTENTION_PAGE_SIZE} shown.`
          : undefined
      }
    >
      <div className="flex flex-col gap-6">
        {/* KPI strip — bets/outcomes movement, decisions awaiting me, blockers. */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Kpi label="Moving bets" value={movingBets} />
          <Kpi label="Active bets" value={data.bets.length} />
          <Kpi label="Key results" value={data.keyResults.length} />
          <Kpi label="Decisions awaiting me" value={data.requestedDecisions.length} />
          <Kpi label="Blockers" value={blockers} />
          <Kpi label="Deliverables" value={data.deliverablesTotal} />
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Left: strategy/KR movement + deliverables (two of three columns). */}
          <div className="flex flex-col gap-6 xl:col-span-2">
            <InspectorSection
              title="Bet movement"
              aside={
                <Button variant="outline" size="sm" onClick={() => go({ to: "/bets" })}>
                  <Icon name="bets" data-icon="inline-start" aria-hidden />
                  All bets
                </Button>
              }
            >
              {data.bets.length === 0 ? (
                <RenderState
                  state="empty"
                  empty={{
                    icon: "bets",
                    title: "No bets yet.",
                    hint: "Commit a strategic bet to see its movement here.",
                  }}
                />
              ) : (
                <div className="flex flex-col gap-2">
                  {data.bets.map((bet) => (
                    <BetRow key={bet.id} bet={bet} />
                  ))}
                </div>
              )}
            </InspectorSection>

            <InspectorSection title="Key result movement">
              {data.keyResults.length === 0 ? (
                <RenderState
                  state="empty"
                  empty={{
                    icon: "okrs",
                    title: "No key results yet.",
                    hint: "Key results appear once objectives are committed under a bet.",
                  }}
                />
              ) : (
                <div className="flex flex-col gap-2">
                  {data.keyResults.map((kr) => (
                    <KeyResultRow key={kr.id} kr={kr} />
                  ))}
                </div>
              )}
            </InspectorSection>
          </div>

          {/* Right: the Attention column — decisions awaiting me + blockers. */}
          <div className="flex flex-col gap-6">
            <InspectorSection
              title="Decisions awaiting me"
              aside={
                <Button variant="outline" size="sm" onClick={reload}>
                  <Icon name="reopen" data-icon="inline-start" aria-hidden />
                  Refresh
                </Button>
              }
            >
              {data.requestedDecisions.length === 0 ? (
                <RenderState
                  state="empty"
                  empty={{
                    icon: "decisions",
                    title: "No decisions awaiting you.",
                    hint: "Requested decisions surface here for you to resolve.",
                  }}
                />
              ) : (
                <div className="flex flex-col gap-2">
                  {data.requestedDecisions.map((decision) => (
                    <DecisionRow key={decision.id} decision={decision} />
                  ))}
                </div>
              )}
            </InspectorSection>

            <InspectorSection
              title="Attention"
              aside={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => go({ to: "/attention-queue" })}
                >
                  <Icon name="actionCards" data-icon="inline-start" aria-hidden />
                  Queue
                </Button>
              }
            >
              {data.attention.length === 0 ? (
                <RenderState
                  state="empty"
                  empty={{
                    icon: "actionCards",
                    title: "Nothing needs you right now.",
                    hint: "Blockers, stale reviews, and high-level action cards appear here.",
                  }}
                />
              ) : (
                <div className="flex flex-col gap-2">
                  {data.attention.map((card) => (
                    <AttentionRow key={card.id} card={card} />
                  ))}
                </div>
              )}
            </InspectorSection>
          </div>
        </div>

        {/* Lower band: deliverable acceptance reading. */}
        <InspectorSection title="Deliverables">
          <FieldGrid>
            <Field label="Total deliverables">{data.deliverablesTotal}</Field>
            <Field label="Moving bets">{movingBets}</Field>
            <Field label="Open blockers">{blockers}</Field>
          </FieldGrid>
        </InspectorSection>
      </div>
    </RenderState>
  );
}

/** True when the portfolio has nothing the executive governs yet. */
function isEmptyPortfolio(data: CockpitData): boolean {
  return (
    data.bets.length === 0 &&
    data.keyResults.length === 0 &&
    data.requestedDecisions.length === 0 &&
    data.attention.length === 0 &&
    data.deliverablesTotal === 0
  );
}

export function ExecutiveCockpit() {
  const { data, loading, error, reload } = useExecutiveCockpit();

  const forbidden = error instanceof ApiError && error.status === 403;
  const state: RenderStateKind = loading
    ? "loading"
    : forbidden
      ? "forbidden"
      : error
        ? "error"
        : data && isEmptyPortfolio(data)
          ? "empty"
          : "normal";

  return (
    <div className="max-w-7xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Executive Cockpit"
          subtitle="Is your strategic will becoming reality? Bet and KR movement, decisions awaiting you, blockers, and deliverables — a curated read over the portfolio."
          icon="dashboard"
        />
        <RenderState
          state={state}
          loadingRows={6}
          error={error}
          onRetry={reload}
          empty={{
            icon: "dashboard",
            title: "No authorized portfolio movement yet.",
            hint: "Commit a bet, set objectives, or request a decision to populate the cockpit.",
          }}
          forbiddenReason="You do not have access to the executive portfolio view."
          forbiddenApprovalPath="an administrator"
        >
          {data && !isEmptyPortfolio(data) ? (
            <CockpitContent data={data} reload={reload} />
          ) : null}
        </RenderState>
      </div>
    </div>
  );
}

export default ExecutiveCockpit;
